/*
 * File: FeedbackDialog.tsx
 * Author: Austin Robinson
 * Date Created: 4/11/2025
 * Copyright (c) DrivingDoc 2025
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DialogProps } from '@toolpad/core/useDialogs';

// import icons
import SuccessCheckmark from 'assets/success_checkmark.gif';

interface FeedbackDialogPayload {
  /**
   * A title for the dialog. Defaults to `'Alert'`.
   */
  title?: React.ReactNode;

  /**
   * The message to show in the dialog.
   */
  message?: React.ReactNode;

  /**
   * The text to show in the "Ok" button. Defaults to `'Ok'`.
   */
  okText?: React.ReactNode;

  /**
   * The icon to show in the dialog. Defaults to `undefined`.
   */
  icon?: 'success';
}

export function FeedbackDialog({ payload, open, onClose }: DialogProps<FeedbackDialogPayload>) {
  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={() => onClose()}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {payload.icon === 'success' && (
            <Box component="img" src={SuccessCheckmark} alt="Success" sx={{ height: '2em', objectFit: 'contain' }} />
          )}
          <Box component="span">{payload.title ?? 'Alert'}</Box>
        </Box>
      </DialogTitle>
      {payload.message && <DialogContent>{payload.message}</DialogContent>}
      <DialogActions>
        <Button disabled={!open} onClick={() => onClose()}>
          {payload.okText ?? 'Ok'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FeedbackDialog;
